import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Switch } from 'antd';
import { Scrollbars, SliderInput } from '@meitu/saas-ui';
import { AppTheme } from '@meitu/saas-ui/es/theme';
import { CustomTitle } from '@meitu/saas-ui/es/title';
import { getShapeTypeConfig, type ShapeTextProps } from '@meitu/saas-editor-core/es/layers/text-layer';
import { imageConverter } from '@meitu/saas-editor-core/es/utils/file-kit';
import { mapRange } from '@meitu/saas-editor-core/es/utils/math-kit';
import { type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import DragPopover from '../../components/drag-popover';
import { TextPanelContext } from '../../context';
import { useStore } from '../../store';
import { type ShapeTextMaterialRaw } from '../../types';
import { Tracker } from '../../utils';
import './style.less';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';
import { TextEditPlugin } from '@meitu/saas-editor-core/es/plugins/text-edit';

type Props = {
    shapeTextList: ShapeTextMaterialRaw[];
    onClose: () => void;
};

export const SHAPE_TEXT_POPOVER = 'shape-text-popover';
export const ShapeTextPanel: React.FC<Props> = ({ shapeTextList, onClose }) => {
    const { theme, isIntl, layerId, recordId } = useContext(TextPanelContext)!;

    const { $t } = useLocaleStore();

    const editor = useEditor();

    // 插入操作组件的索引
    const [insertIndex, setInsertIndex] = useState<number>(-1);

    const draggleRef = useRef<HTMLDivElement>(null);
    const scrollbarsRef = useRef<any>(null);

    const __shapeExtension = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        return layerData.shapeExtension;
    });

    const shapeExtensionCache = useStore((state) => state.shapeExtensionCache);

    const shapeExtension = __shapeExtension || shapeExtensionCache;

    const shapeType = shapeExtension ? shapeExtension.curveTextType : null;

    const [shapeTextProps, setShapeTextProps] = useState<ShapeTextProps[] | null>(
        shapeType ? cloneDeep(getShapeTypeConfig()[shapeType]) : null,
    );

    const cancelShapeText = () => {
        setInsertIndex(-1);
        editor.getInstance(TextLayerExtension).cancelShapeEffect(layerId);
        setShapeTextProps(null);

        Tracker.cancelShapeText({
            recordId,
            stageType: editor.stage.getType(),
        });
    };

    const applyShapeText = async (data: ShapeTextMaterialRaw) => {
        const editingLayer = editor.getInstance(TextEditPlugin).getEditingLayer();
        if (editingLayer) {
            await editor.getInstance(TextEditPlugin).exitEditing();
        }

        const shapeType = data['shape_type'];
        const materialID = data['id'];

        editor.getInstance(TextLayerExtension).applyShapeText(layerId, shapeType, { materialID });
        setShapeTextProps(cloneDeep(getShapeTypeConfig()[shapeType]));

        // eslint-disable-next-line react-hooks/rules-of-hooks
        Tracker.useShapeText({
            recordId,
            stageType: editor.stage.getType(),
            shapeType,
            materialID,
        });
    };

    const adjustShapeTextProps = async (
        props: ShapeTextProps,
        name: string,
        value: number | boolean,
        options = { writeHistory: true },
    ) => {
        if (!shapeTextProps) return;

        const nextProps = shapeTextProps.map((item) => {
            if (item.param === name) {
                return {
                    ...item,
                    value,
                };
            }
            return item;
        }) as ShapeTextProps[];

        setShapeTextProps(nextProps);

        const { writeHistory } = options;
        editor.getInstance(TextLayerExtension).adjustShapeTextProps(layerId, name, value, { writeHistory, props });

        if (writeHistory) {
            Tracker.adjustShapeTextProps({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    useEffect(() => {
        shapeTextList.forEach((v) => {
            window._stat_track('tpbj_material_show', {
                type_id: 1088,
                material_id: v.id,
            });
        });
    }, [shapeTextList]);

    useEffect(() => {
        setShapeTextProps(shapeType ? cloneDeep(getShapeTypeConfig()[shapeType]) : null);
    }, [shapeType]);

    useEffect(() => {
        if (!shapeExtension) return;
        const index = shapeTextList.findIndex((item) => item['shape_type'] === shapeExtension.curveTextType);
        if (index === -1) return;
        const activeIndex = index + 1; //+1 是因为第一个元素是自定义的重置元素
        const insertIndex = Math.floor(activeIndex / 3) * 3 + 2;
        const lastElIndex = shapeTextList.length;
        setInsertIndex(insertIndex >= lastElIndex ? lastElIndex : insertIndex); // 设置插入操作组件的索引

        const activeEl = document.querySelector(`.shape-text-panel__item:nth-of-type(${activeIndex})`) as HTMLElement;
        if (activeEl) requestAnimationFrame(() => scrollbarsRef.current?.scrollTop(activeEl.offsetTop - 16)); // 减去16px，是因为操作组件的padding-top为16px
    }, [shapeExtension?.curveTextType]);

    //  渲染操作组件
    const renderActions = () => {
        return shapeTextProps && shapeExtension ? (
            <div
                className={classNames('shape-text-panel__actions', {
                    'shape-text-panel__actions--intl': isIntl,
                })}
            >
                {shapeTextProps.map((item, idx) => {
                    if ('isBool' in item) {
                        return (
                            <LabelAction key={idx} title={item.label} align>
                                <div className="shape-text-panel__control">
                                    <Switch
                                        checked={item.value}
                                        onChange={() => adjustShapeTextProps(item, item.param, !item.value)}
                                    />
                                </div>
                            </LabelAction>
                        );
                    }

                    return (
                        <LabelAction key={item.label} title={item.label} align>
                            <div className="shape-text-panel__control">
                                <SliderInput
                                    type="horizontal"
                                    showStepBtn
                                    min={item.range[0]}
                                    max={item.range[1]}
                                    precision={1}
                                    value={mapRange(shapeExtension[item.param], item.ratio, item.range)}
                                    onChange={(v) => adjustShapeTextProps(item, item.param, v, { writeHistory: false })}
                                    onAfterChange={(v) =>
                                        adjustShapeTextProps(item, item.param, v, { writeHistory: true })
                                    }
                                />
                            </div>
                        </LabelAction>
                    );
                })}
            </div>
        ) : null;
    };
    return (
        <DragPopover draggleRef={draggleRef} innerDoms={['shape-text-panel__control']}>
            <div className="shape-text-panel" ref={draggleRef}>
                <div className="shape-text-panel__title">
                    <CustomTitle
                        leftNode={<div className="left">{$t((v) => v.panel.textPanel.transform)}</div>}
                        rightNode={
                            <div className="right" onClick={onClose}>
                                <IconFont name="CrossBlack" fontSize={12} color="var(--content-system-secondary)" />
                            </div>
                        }
                    />
                </div>
                {/* safari低版本需要包一层div 滚动列表才能撑开*/}
                <div style={{ flex: 1 }}>
                    <Scrollbars autoHide scrollbarsRef={scrollbarsRef}>
                        <div className="shape-text-panel__list">
                            <div className="shape-text-panel__item" onClick={cancelShapeText}>
                                <IconFont name="SlashCircle" fontSize={38} />
                            </div>

                            {shapeTextList.map((item, index) => {
                                const thumbnailKey =
                                    `thumbnail${theme === AppTheme.white ? '' : '_black'}` as keyof ShapeTextMaterialRaw;
                                const thumbnail =
                                    (item[thumbnailKey] as ShapeTextMaterialRaw['thumbnail'])?.url ||
                                    item.thumbnail.url;

                                const isActive = shapeExtension
                                    ? shapeExtension.curveTextType === item['shape_type']
                                    : false;

                                return (
                                    <React.Fragment key={item.id}>
                                        <div
                                            data-curve-type={item['shape_type']}
                                            className={classNames('shape-text-panel__item', {
                                                'shape-text-panel__item--active': isActive,
                                            })}
                                            onClick={() => applyShapeText(item)}
                                        >
                                            <img src={imageConverter(thumbnail, 140)} alt="" />
                                        </div>
                                        {/* 插入操作组件 */}
                                        {insertIndex - 1 === index && <p>{renderActions()}</p>}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </Scrollbars>
                </div>
            </div>
        </DragPopover>
    );
};
